<script setup>
import { computed, inject, toRef } from 'vue';
import { currency } from '@bignerve/ui-utils';
import { useDetails } from '/app/composables/challenge';
import { useFormContext } from '/app/composables/form';
import { useBus } from '/app/composables/bus';
import { useAuth } from '/app/composables/auth';
import ChallengeTitle from '/app/components/challenge/Title.vue';
import ChallengeImage from '/app/components/challenge/Image.vue';
import NIcon from '/app/components/NIcon.vue';
import NPlayerDetails from '/app/components/game/players/Details.vue';
import NTimeRemaining from '/app/components/TimeRemaining.vue';

const props = defineProps({
    challenge: {
        type: Object,
        required: true,
    },
});

const context = inject('context');
const builder = inject('builder');

const { auth: $auth } = useAuth();
const form = useFormContext();
const { emit: bus } = useBus();

useDetails(toRef(props, 'challenge'));

const routeToDetail = computed(() => props.challenge.slug ? {
    name: 'challenges.show',
    params: { challenge: props.challenge.slug },
} : null);

function shareOn(state) {
    bus('challenge:creation-flow:preview:share-on', state);
}
</script>

<template>
    <div >
        <article class="challenge-card is-preview">
            <ChallengeImage
                :challenge="challenge"
                :category="context.categorySelected && context.categorySelected.name"
                :credits="true"
                :to="routeToDetail"
                :show-loading="true"
                :prefetch="true"
                class="challenge-card__image-wrapper"
            />
            
            <div class="challenge-card__body">
                <NPlayerDetails :user="$auth.user" class="mb-2" />
                
                <ChallengeTitle
                    :challenge="challenge"
                    :to="routeToDetail"
                    class="text-xl font-semibold leading-6 text-dust-800"
                />
                
                <div v-if="challenge.co_author" v-tooltip="'Co-Author'" class="flex items-center space-x-2">
                    <NIcon as="qq" class="text-innovator text-2xl" />
                    <NPlayerDetails :user="challenge.co_author" />
                </div>
                    
                <NTimeRemaining
                    v-if="form.fields.challenge.duration_end"
                    :start-date="form.fields.challenge.duration_start"
                    :end-date="form.fields.challenge.duration_end"
                    :closed="form.fields.challenge.is_closed"
                    style="opacity: 0.7"
                />
            </div>

            <div v-if="form.fields.challenge.innovation_scale && ! builder.isCaption && ! builder.isQQ" class="p-3 flex justify-center">
                <img class="h-7" :src="`/img/innovative-level/${form.fields.challenge.innovation_scale}.png`" :alt="form.fields.challenge.innovation_scale">
            </div>
            <div  class="ml-3 mr-3 mt-2 mb-2 flex flex-wrap items-center justify-between gap-2">
                <a href="#" class="challenge-card__footer-item whitespace-nowrap">
                    <span aria-hidden="true" class="icon icon-lightbulb-o1"></span>
                    Ideas (0)
                </a>
                <span v-if="form.fields.prize?.prize_money" :class="{ disabled: !currency(form.fields.prize?.prize_money || 0) }" class="challenge-card__footer-item whitespace-nowrap">
                    <span aria-hidden="true" class="icon icon-money-bill-alt-regular"></span>
                    ${{ currency(form.fields.prize?.prize_money || 0) }}
                </span>
                <span v-if="form.fields.prize?.prize_type" class="challenge-card__footer-item whitespace-nowrap">
                    <span aria-hidden="true" class="icon icon-trophy-solid"></span>
                    {{ $t(`challenges.prize-type.${form.fields.prize.prize_type}`) }}
                </span>
                <div v-if="form.fields.challenge.slug" class="relative challenge-card__footer-item pulsating-circle is-touchable whitespace-nowrap"
                    data-info="This is the Share button to help you promote your challenges" @mouseover="shareOn(true)" @mouseleave="shareOn(false)">
                    <span aria-hidden="true" class="icon icon-share-square-solid"></span>
                </div>
            </div>
        </article>
    </div>
</template>
